import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { brandColors } from '../../theme';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'info' | 'ghost' | 'outline' | 'link' | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
}

const getVariantClasses = (variant: ButtonVariant): string => {
  switch (variant) {
    case 'secondary':
      return 'bg-gray-100 text-gray-700 border-transparent';
    case 'ghost':
      return 'bg-transparent text-gray-700 border-transparent';
    case 'outline':
      return 'bg-white text-gray-700 border-gray-300';
    case 'icon':
      return 'bg-transparent text-gray-700 border-transparent';
    default:
      return '';
  }
};

// Use inline styles only for brand colors (primary, danger, success, info, link)
const getVariantStyles = (variant: ButtonVariant): React.CSSProperties | undefined => {
  switch (variant) {
    case 'primary':
      return {
        backgroundColor: brandColors.primary,
        color: 'white',
        borderColor: 'transparent',
      };
    case 'danger':
      return {
        backgroundColor: brandColors.error,
        color: 'white',
        borderColor: 'transparent',
      };
    case 'success':
      return {
        backgroundColor: brandColors.success,
        color: 'white',
        borderColor: 'transparent',
      };
    case 'info':
      return {
        backgroundColor: brandColors.info,
        color: 'white',
        borderColor: 'transparent',
      };
    case 'link':
      return {
        backgroundColor: 'transparent',
        color: brandColors.primary,
        borderColor: 'transparent',
      };
    default:
      return undefined;
  }
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      disabled,
      className = '',
      children,
      style,
      type = 'button',
      ...props
    },
    ref
  ) => {
    // Link variant has minimal styling - no padding, no border, underline on hover
    const isLinkVariant = variant === 'link';
    // Icon variant needs square aspect ratio and minimal padding
    const isIconVariant = variant === 'icon';

    const baseStyles = isLinkVariant
      ? 'inline-flex items-center justify-center font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed hover:underline'
      : 'inline-flex items-center justify-center rounded-md font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed border hover:opacity-90';

    // Icon variant uses square padding instead of size styles
    const iconSizeStyles: Record<ButtonSize, string> = {
      sm: 'p-1',
      md: 'p-2',
      lg: 'p-3',
    };

    const widthStyle = fullWidth && !isLinkVariant && !isIconVariant ? 'w-full' : '';

    // Determine size/padding styles based on variant
    const getSizeStyles = (): string => {
      if (isLinkVariant) return '';
      if (isIconVariant) return iconSizeStyles[size];
      return sizeStyles[size];
    };

    const combinedClassName = [
      baseStyles,
      getSizeStyles(),
      widthStyle,
      getVariantClasses(variant),
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const brandStyles = getVariantStyles(variant);
    const combinedStyle = brandStyles ? { ...brandStyles, ...style } : style;

    return (
      <button
        ref={ref}
        type={type}
        className={combinedClassName}
        style={combinedStyle}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-30"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-90"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
